import type React from "react";
import { useMemo } from "react";

import { PlainTooltip } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { AutocompleteWithTabs } from "pro-features/components/AutocompleteWithTabs/AutocompleteWithTabs";
import type { IListItem } from "pro-features/components/AutocompleteWithTabs/handlers";
import { ESelectionType } from "pro-features/components/AutocompleteWithTabs/types";
import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";
import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { translate } from "app/filters/i18nFilter";
import { isModuleSupportSegments } from "app/pages/industry-analysis/common/isModuleSupportSegments";
import { useBuilderContext } from "app/pages/industry-analysis/market-builder/builderContext";
import { customSegmentsSectionHeaderHOF } from "app/pages/industry-analysis/market-builder/components/entitiesAutocomplete/CustomSegmentsSectionHeaderHOF";
import { ESelectDomainOptions } from "app/pages/industry-analysis/market-builder/MarketBuilder.types";
import type { IDomain } from "app/pages/industry-analysis/market-builder/MarketBuilder.types";
import {
    AutoCompleteContainer,
    AutocompleteLabel,
} from "app/pages/industry-analysis/market-builder/tabs/common/styled";
import type { OnAddMultipleWebsites } from "app/pages/industry-analysis/market-builder/tabs/types";
import { isSegmentsLimitationExceeded } from "app/pages/segments/metering/SegmentsMetering";
import { allTrackers } from "app/services/track/track";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { MAX_DOMAINS_ALLOWED } from "../../services/ImportDomainsHandler";

import { EntitiesAutocompleteWrapper, StyledAutocompletePlaceholder } from "./StyledComponents";

interface IEntitiesAutocompleteProps {
    excludeEntities: IDomain[];
    onAddEntity: (domain: IDomain) => void;
    onPaste: (clipboardEvent: ClipboardEvent) => void;
    isDisabled: boolean;
    isSegmentsLimitReached: () => boolean;
    onAddMultipleWebsites: OnAddMultipleWebsites;
    onBuildSegmentClick: VoidFunction;
    getSuggestedDomains: (domains?: IDomain[]) => void;
    showSuggestedBtn: boolean;
    isSegmentsLoading: boolean;
}

export const EntitiesAutocomplete: React.FunctionComponent<IEntitiesAutocompleteProps> = (props) => {
    const {
        excludeEntities,
        onAddEntity,
        onPaste,
        isDisabled,
        isSegmentsLimitReached,
        onAddMultipleWebsites,
        onBuildSegmentClick,
        getSuggestedDomains,
        showSuggestedBtn,
        isSegmentsLoading,
    } = props;

    const { openAutocompleteOnStart } = useBuilderContext();

    const isLimitationExceeded = useExceededLimitation(isSegmentsLimitationExceeded);

    const MAX_SEGMENTS_IN_CATEGORY = swSettings.components.IndustryAnalysis.resources.MaxNumOfSegments;

    const addDomain = (item) => {
        const { domain, segmentName, type, favicon, id } = item;
        onAddEntity({
            segmentName: segmentName,
            domain: domain,
            favicon: favicon,
            type: type,
            id: id,
        });
    };

    const autocompleteCommonProps = useMemo(() => {
        return {
            autoFocus: false,
            isFocused: false,
            searchIcon: "add",
            top: "0",
            width: "100%",
            position: "static",
            zIndex: 1,
            onPaste: isSegmentsLoading ? undefined : onPaste, // Prevent paste while segments loading
        };
    }, [onPaste, isSegmentsLoading]);

    const autocompletePlaceholder = (isSegments = false) => {
        return (
            <StyledAutocompletePlaceholder dangerouslySetInnerHTML={true}>
                {isSegments
                    ? "market.builder.websites.autocomplete.placeholder.segments"
                    : "customcategories.wizard.editor.placeholder.bold"}
            </StyledAutocompletePlaceholder>
        );
    };

    const autocompleteWithSegmentsProps = {
        preventTruncateUnlessForced: true,
        placeholder: autocompletePlaceholder(true),
        ...autocompleteCommonProps,
    };

    const onBuildSegmentClickWithTracking = () => {
        onBuildSegmentClick();
        allTrackers.trackEvent("Button", "click", `By websites/Create new segment/No segment`);
    };
    const showCustomTabsList = categoryService.hasAdvancedCustomIndustriesPermission() && isModuleSupportSegments();
    const tabsProps = {
        showAccountSegments: false,
        disabledTabText: translate("market.builder.domains.autocomplete.segments.limit"),
        showCustomTabsList,
        CustomSegmentsSectionHeader: customSegmentsSectionHeaderHOF(onBuildSegmentClick, isLimitationExceeded),
        onBuildSegmentClick: onBuildSegmentClickWithTracking,
    };

    const assetToDomain = (asset: IAsset): IDomain => {
        const { displayText, image, name, id, type } = asset;
        return {
            domain: displayText,
            favicon: image,
            type: type === EAssetType.Segment ? EAssetType.Segment : EAssetType.Website,
            id,
            segmentName: name,
        };
    };

    const onSelect = (item: IAsset | Array<IAsset>) => {
        if (Array.isArray(item)) {
            const numberOfSegments = item.length;
            allTrackers.trackEvent("Button", "click", `By websites/addSegment/${numberOfSegments}`);
            const domains = item.map(assetToDomain);
            onAddMultipleWebsites(domains, false, ESelectDomainOptions.MANUAL);
            if (!showSuggestedBtn) {
                getSuggestedDomains(domains);
            }
        } else {
            addDomain(assetToDomain(item));
        }
    };

    const getExcludeItems = (): IAsset[] => {
        return excludeEntities.map((entity: IDomain): IAsset => {
            return {
                displayText: entity.domain,
                image: entity.favicon,
                type: entity.type,
                id: entity.id,
                name: entity.segmentName,
            };
        });
    };
    const numberOfSelectableEntities = Math.min(MAX_SEGMENTS_IN_CATEGORY, MAX_DOMAINS_ALLOWED - excludeEntities.length);
    const selectedSegmentsAmount = excludeEntities.filter(
        (excludeEntity) => excludeEntity.type === EAssetType.Segment,
    ).length;

    const numberOfSelectableSegments = MAX_SEGMENTS_IN_CATEGORY - selectedSegmentsAmount;

    const selectionTypeProps = {
        selectionTypes: {
            [EAssetType.Website]: ESelectionType.SINGLE,
            [EAssetType.Segment]: ESelectionType.MULTIPLE,
            [EAssetType.Company]: ESelectionType.MULTIPLE,
        },
        numberOfSelectableEntities,
        numberOfSelectableSegments,
        callbackOnCheckboxClick: (isChecked: boolean, listItem: IListItem) => {
            const { name, type, id, displayText } = listItem;
            if (type !== EAssetType.Segment) {
                return;
            }
            allTrackers.trackEvent(
                "Checkbox",
                isChecked ? "add" : "remove",
                `By websites/addSegment/${displayText} (${name}), ${id}`,
            );
        },
    };

    return (
        <AutoCompleteContainer>
            <AutocompleteLabel>{translate("market.builder.domains.autocomplete.title")}</AutocompleteLabel>
            <PlainTooltip
                cssClass="plainTooltip-element"
                tooltipContent={
                    isSegmentsLoading
                        ? translate("market.builder.domains.autocomplete.segments.loading")
                        : translate("marketbuilder.import.disable.tooltip")
                }
                placement="top"
                enabled={isDisabled || isSegmentsLoading}
            >
                <EntitiesAutocompleteWrapper data-automation="entities-autocomplete">
                    <AutocompleteWithTabs
                        onSelect={onSelect}
                        excludeItems={getExcludeItems()}
                        disabled={isDisabled || isSegmentsLoading}
                        shouldDisableSegmentsTab={isSegmentsLimitReached}
                        searchIcon={"add"}
                        tabsProps={tabsProps}
                        autocompleteProps={autocompleteWithSegmentsProps}
                        isDisabledListItemSupported={true}
                        selectionTypeProps={selectionTypeProps}
                        showLinesOfBusiness={true}
                    />
                </EntitiesAutocompleteWrapper>
            </PlainTooltip>
        </AutoCompleteContainer>
    );
};
